import React, { useState } from 'react';
import { OnboardingStatusResponse } from '../types';

interface OnboardingContactProps {
  token: string;
  onboardingData: OnboardingStatusResponse;
  onStepComplete: (step: string, data?: Record<string, unknown>) => Promise<void>;
  error: string | null;
}

/**
 * Contact information step for Fair Manager onboarding
 * Issue #1455 - Step 4 of 7: Public email, phone, show contact toggle (all optional)
 */
const OnboardingContact: React.FC<OnboardingContactProps> = ({
  onboardingData,
  onStepComplete,
}) => {
  const savedContact = onboardingData.onboarding.progress_data?.contact || {};

  const [publicEmail, setPublicEmail] = useState(savedContact.public_email || '');
  const [publicPhone, setPublicPhone] = useState(savedContact.public_phone || '');
  const [showContactInfo, setShowContactInfo] = useState(savedContact.show_contact_info || false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      await onStepComplete('contact', {
        public_email: publicEmail.trim(),
        public_phone: publicPhone.trim(),
        show_contact_info: showContactInfo,
      });

      // Move to next step
      await onStepComplete('payment');
    } catch (err) {
      setError('Failed to save contact information. Please try again.');
      console.error('Failed to save contact:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setLoading(true);
      await onStepComplete('payment');
    } catch (err) {
      console.error('Failed to skip contact:', err);
      setError('Failed to proceed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact Information</h2>
        <p className="text-gray-600">
          Add contact information so vendors can reach you. All fields are optional and can be
          updated later.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
        <div className="flex items-start">
          <svg
            className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-blue-900">Privacy Notice</h4>
            <p className="text-sm text-blue-800 mt-1">
              This information will only be visible to vendors if you enable "Show contact
              information on fair listings" below. Your account email remains private.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Public Email */}
        <div className="mb-6">
          <label htmlFor="publicEmail" className="block text-sm font-medium text-gray-700 mb-2">
            Public Email (Optional)
          </label>
          <input
            type="email"
            id="publicEmail"
            value={publicEmail}
            onChange={(e) => {
              setPublicEmail(e.target.value);
              setError(null);
            }}
            placeholder="info@yourfair.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            aria-describedby="publicEmail-description"
          />
          <p id="publicEmail-description" className="mt-1 text-sm text-gray-500">
            An email where vendors can contact you about fair inquiries
          </p>
        </div>

        {/* Public Phone */}
        <div className="mb-6">
          <label htmlFor="publicPhone" className="block text-sm font-medium text-gray-700 mb-2">
            Public Phone (Optional)
          </label>
          <input
            type="tel"
            id="publicPhone"
            value={publicPhone}
            onChange={(e) => {
              setPublicPhone(e.target.value);
              setError(null);
            }}
            placeholder="(555) 123-4567"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            aria-describedby="publicPhone-description"
          />
          <p id="publicPhone-description" className="mt-1 text-sm text-gray-500">
            A phone number where vendors can reach you
          </p>
        </div>

        {/* Show Contact Info Toggle */}
        <div className="mb-6">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={showContactInfo}
              onChange={(e) => setShowContactInfo(e.target.checked)}
              className="mt-1 h-5 w-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              aria-describedby="showContact-description"
            />
            <span className="ml-3">
              <span className="block text-sm font-medium text-gray-700">
                Show contact information on fair listings
              </span>
              <span id="showContact-description" className="block text-sm text-gray-500 mt-1">
                When enabled, vendors will see your public email and phone on your fair detail
                pages
              </span>
            </span>
          </label>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4" role="alert">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={handleSkip}
            disabled={loading}
            className="text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
          >
            Skip for now
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`
              px-6 py-2 rounded-md font-medium transition-colors
              ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
              }
            `}
          >
            {loading ? 'Saving...' : 'Save and Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OnboardingContact;
